import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useRouter } from "@tanstack/react-router";
import { createDefaultSet } from "@/lib/utils/workoutSession";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc/client";
import {
  patchKeyedListCache,
  patchListCache,
  patchQueryCache,
} from "@trainrr/api-client";

type WorkoutSetLike = {
  set_num?: number | null;
  reps?: number | null;
  weight?: number | null;
  time_seconds?: number | null;
  distance_meters?: number | null;
};

type ExerciseLike = {
  id: string;
  tracking_type?: string | null;
  ex_order?: number | null;
  sub_order?: number | null;
  sets?: WorkoutSetLike[];
};

type WorkoutLogLike = {
  name?: string | null;
  workout_name?: string | null;
  workout_description?: string | null;
  exercises?: ExerciseLike[];
};

type AddWorkoutToPlanDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workoutLog?: WorkoutLogLike | null;
  clientId: string;
  orgId?: string | null;
};

const getWorkoutName = (workoutLog?: WorkoutLogLike | null) =>
  workoutLog?.name ?? workoutLog?.workout_name ?? "Workout";

export default function AddWorkoutToPlanDialog({
  open,
  onOpenChange,
  workoutLog,
  clientId,
  orgId,
}: AddWorkoutToPlanDialogProps) {
  const router = useRouter();
  const utils = trpc.useUtils();
  const [addToPlanName, setAddToPlanName] = useState("");
  const [selectedPlanId, setSelectedPlanId] = useState<string | undefined>();
  const [isSavingToPlan, setIsSavingToPlan] = useState(false);
  const saveWorkout = trpc.workouts.save.useMutation();
  const addWorkoutToPlan = trpc.training.addWorkoutToPlan.useMutation();

  const { data: planRows } = trpc.training.listClientPlanOptions.useQuery(
    { clientId },
    { enabled: open },
  );

  const planOptions = useMemo(() => {
    return (planRows ?? []).map((row) => ({
      id: row.id,
      name: row.name,
      is_primary: row.is_primary,
    }));
  }, [planRows]);

  useEffect(() => {
    if (!open) {
      setSelectedPlanId(undefined);
      setAddToPlanName("");
      return;
    }
    if (!addToPlanName) {
      setAddToPlanName(getWorkoutName(workoutLog));
    }
    if (!selectedPlanId && planOptions.length > 0) {
      const primary =
        planOptions.find((plan) => plan.is_primary) ?? planOptions[0];
      setSelectedPlanId(primary.id);
    }
  }, [open, addToPlanName, workoutLog, planOptions, selectedPlanId]);

  const handleConfirmAddToPlan = async () => {
    if (!workoutLog) return;
    if (!orgId) {
      toast.error("No organisation found for this workout.");
      return;
    }
    if (!selectedPlanId) {
      toast.error("Select a plan to assign this workout.");
      return;
    }

    setIsSavingToPlan(true);
    const exercises = (workoutLog.exercises ?? []).map((exercise) => {
      const orderedSets = [...(exercise.sets ?? [])].sort(
        (a, b) => (a.set_num ?? 0) - (b.set_num ?? 0)
      );
      return {
        exercise_id: exercise.id,
        ex_order: exercise.ex_order ?? 0,
        sub_order: exercise.sub_order ?? 0,
        position: exercise.ex_order ?? 0,
        superset_key: null,
        superset_position: exercise.sub_order ?? 0,
        sets: orderedSets.length
          ? orderedSets.map((set) => ({
              reps: set.reps ?? null,
              weight: set.weight ?? null,
              time_seconds: set.time_seconds ?? null,
              distance_meters: set.distance_meters ?? null,
            }))
          : [createDefaultSet(exercise.tracking_type)],
        tempo: "",
        rir: 0,
        rest_time: 0,
      };
    });

    let newWorkout: Record<string, unknown>;
    try {
      newWorkout = await saveWorkout.mutateAsync({
        payload: {
          workout: {
            org_id: orgId,
            name: addToPlanName.trim() || getWorkoutName(workoutLog),
            description: workoutLog.workout_description ?? "",
            is_template: false,
            exercises: exercises as Record<string, unknown>[],
          },
        },
      });
    } catch (workoutError) {
      console.error("Error creating workout", workoutError);
      toast.error("Error creating workout");
      setIsSavingToPlan(false);
      return;
    }

    const created =
      newWorkout.workout &&
      typeof newWorkout.workout === "object" &&
      "id" in newWorkout.workout
        ? (newWorkout.workout as { id?: string }).id
        : undefined;
    const newWorkoutId =
      (typeof created === "string" ? created : null) ??
      (typeof newWorkout.id === "string" ? newWorkout.id : null);

    if (!newWorkoutId) {
      toast.error("Error creating workout");
      setIsSavingToPlan(false);
      return;
    }

    try {
      const assigned = await addWorkoutToPlan.mutateAsync({
        planId: selectedPlanId,
        workoutId: newWorkoutId,
        day: 0,
      });
      patchKeyedListCache(utils.training.listForClient, { clientId }, (old) =>
        old.map((plan) =>
          plan.id === assigned.planId
            ? { ...plan, workouts: assigned.workouts }
            : plan,
        ),
      );
      patchListCache(utils.training.listTemplates, (old) =>
        old.map((row) =>
          row.id === assigned.planId
            ? { ...row, workoutCount: assigned.workouts.length }
            : row,
        ),
      );
      patchQueryCache(
        utils.training.getPlan,
        { planId: assigned.planId },
        (current) => ({ ...current, workouts: assigned.workouts }),
      );
    } catch (planError) {
      console.error("Error assigning workout to plan", planError);
      toast.error("Error assigning workout to plan");
      setIsSavingToPlan(false);
      return;
    }

    await router.invalidate();

    setIsSavingToPlan(false);
    onOpenChange(false);
    toast.success("Workout added to plan");
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      analyticsName="add_workout_to_plan_dialog"
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add workout to plan</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input
              value={addToPlanName}
              onChange={(event) => setAddToPlanName(event.target.value)}
              placeholder="Workout name"
            />
          </div>
          <div className="space-y-2">
            <Label>Plan</Label>
            <Select
              value={selectedPlanId ?? ""}
              onValueChange={setSelectedPlanId}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a plan" />
              </SelectTrigger>
              <SelectContent>
                {planOptions.map((plan) => (
                  <SelectItem key={plan.id} value={plan.id}>
                    {plan.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {planOptions.length === 0 && (
              <p className="text-xs text-muted-foreground">
                No plans available for this client.
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAddToPlan}
            disabled={isSavingToPlan || planOptions.length === 0}
          >
            {isSavingToPlan ? "Saving..." : "Save to plan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
